import { useEffect, useMemo, useRef, useState } from "react";
import { Plus } from "lucide-react";
import { useTran, useFormDirty } from "@/hooks";
import { useAuthStore } from "@/features/auth/hooks";
import AppDialog from "@/components/common/AppDialog";
import { OrgTreeSelect } from "@/components/common/OrgTreeSelect";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { SelectField } from "@/components/common";
import {
  makeTreewithAllowOrgIds,
  toApiOrgId,
} from "../../utils/orgTree.helpers";
import {
  useDisclosureStore,
  useEntryDataStore,
  useEntryUIStore,
  useValidationStore,
} from "../../hooks/useDataEntryStore";
import { bulkCreateDataEntries } from "../../operations/entry.ops";
import { observer } from "mobx-react-lite";
import { reportingYearStore } from "@/features/reporting-boundary/stores/reporting-year.store";
import { formatReportingYearPeriod } from "@/features/esg/utils/period-constants";
import { EntryRowTable } from "../entries";

const MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const emptyEntry = (defaultUnit = "") => ({
  id: crypto.randomUUID(),
  label: "",
  entryDate: "",
  value: "",
  unit: defaultUnit,
  year: "",
  location: "",
  category: "",
  denominator: "",
  reportingMonth: "",
  remark: "",
});

const getReportingYearId = (reportingYear = {}) =>
  reportingYear._id || reportingYear.id || reportingYear.reportingYearId;

const getYearsInReportingYear = (reportingYear = {}) => {
  const startYear = Number(reportingYear.startYear);
  const endYear = Number(reportingYear.endYear ?? reportingYear.startYear);
  if (!Number.isFinite(startYear) || !Number.isFinite(endYear)) return [];

  const years = [];
  for (let year = startYear; year <= endYear; year += 1) {
    years.push(year);
  }
  return years;
};

const getOrganizationId = (organization) =>
  organization && typeof organization === "object"
    ? organization._id || organization.id || organization.organizationId
    : organization;

const buildAllowedOrganizationSet = ({
  records = [],
  reportingYear,
  selectedYear,
  companyId,
}) => {
  if (!selectedYear || !reportingYear) return null;

  const combine = (left, right) => {
    if (!left) return right;
    if (!right) return left;
    return new Set([...left].filter((id) => right.has(id)));
  };

  const reportingYearScope = reportingYear.isAllOrganization
    ? null
    : new Set(
        [
          ...(reportingYear.isCompanySelected && companyId ? [companyId] : []),
          ...(reportingYear.organizations || []).map(getOrganizationId),
        ]
          .filter(Boolean)
          .map(String),
      );

  const yearRecords = records.filter(
    (record) => Number(record.year) === Number(selectedYear),
  );
  const disclosureHasAllOrganizations = yearRecords.some(
    (record) => record.isAllOrganization,
  );
  const disclosureScope = disclosureHasAllOrganizations
    ? null
    : new Set(
        yearRecords
          .flatMap((record) => [
            ...(record.isCompanySelected && companyId ? [companyId] : []),
            ...(record.organizationIds || []),
          ])
          .filter(Boolean)
          .map(String),
      );

  return combine(reportingYearScope, disclosureScope);
};

const CreateEntriesDialog = observer(() => {
  const { t } = useTran();
  const { user } = useAuthStore();
  const { activeDisclosure, reportingBoundary } = useDisclosureStore();
  const { reportingYears } = reportingYearStore;
  const { disclosureOrgTree } = useEntryDataStore();

  const {
    showCreateEntriesDialog,
    creatingEntries,
    setShowCreateEntriesDialog,
    createDialogPreset,
    setCreateDialogPreset,
  } = useEntryUIStore();
  const { errors, validateEntries, clearErrors, clearEntryFieldError } =
    useValidationStore();

  const {
    _id,
    code,
    title,
    inputType,
    carbonEmissionMultiplier,
    masterDataYears = [],
    masterDataRecords = [],
  } = activeDisclosure || {};

  const unitOptions = activeDisclosure?.unitOptions || [];
  const defaultUnit = activeDisclosure?.defaultUnit || unitOptions[0] || "";
  const [organizationId, setOrganizationId] = useState("all");
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedReportingYearId, setSelectedReportingYearId] = useState("");
  const [showYearError, setShowYearError] = useState(false);
  const [selectedReportingYear, setSelectedReportingYear] = useState(null);

  const presetRef = useRef(createDialogPreset);
  presetRef.current = createDialogPreset;

  useEffect(() => {
    if (showCreateEntriesDialog && presetRef.current) {
      if (presetRef.current.orgId) setOrganizationId(presetRef.current.orgId);
      if (presetRef.current.year) {
        setSelectedYear(String(presetRef.current.year));
      }
    }
  }, [showCreateEntriesDialog]);

  const reportingYearOptions = useMemo(() => {
    const allowedYears = new Set(
      (masterDataYears || [])
        .map((year) => Number(year))
        .filter(Number.isFinite),
    );

    return (reportingYears || [])
      .filter((reportingYear) => {
        const startYear = Number(reportingYear.startYear);

        if (!Number.isFinite(startYear)) return false;

        return allowedYears.has(startYear);
      })
      .sort((a, b) => Number(b.startYear) - Number(a.startYear))
      .map((reportingYear) => ({
        value: String(getReportingYearId(reportingYear)),
        label: formatReportingYearPeriod(reportingYear),
      }));
  }, [masterDataYears, reportingYears]);

  const availableOrgTree = useMemo(() => {
    const allowedSet = buildAllowedOrganizationSet({
      records: masterDataRecords,
      reportingYear: selectedReportingYear,
      selectedYear,
      companyId: user?.companyId,
    });

    return allowedSet
      ? makeTreewithAllowOrgIds(disclosureOrgTree, allowedSet)
      : disclosureOrgTree;
  }, [
    disclosureOrgTree,
    masterDataRecords,
    selectedReportingYear,
    selectedYear,
    user?.companyId,
  ]);

  useEffect(() => {
    if (!showCreateEntriesDialog || selectedReportingYearId || !selectedYear) {
      return;
    }

    const selectedReporting = reportingYears.find((reportingYear) =>
      getYearsInReportingYear(reportingYear).includes(Number(selectedYear)),
    );

    if (!selectedReporting) return;

    setSelectedReportingYearId(String(getReportingYearId(selectedReporting)));
    setSelectedReportingYear(selectedReporting);
  }, [
    reportingYears,
    selectedReportingYearId,
    selectedYear,
    showCreateEntriesDialog,
  ]);

  const [entries, setEntries] = useState(() => [emptyEntry(defaultUnit)]);

  const formValues = useMemo(
    () => ({ entries, organizationId, selectedYear, selectedReportingYearId }),
    [entries, organizationId, selectedYear, selectedReportingYearId],
  );
  const isDirty = useFormDirty(formValues, showCreateEntriesDialog);

  const yearHasError = showYearError && !selectedYear;
  const yearError = yearHasError ? `${t("It is required")}` : "";

  const updateEntry = (id, field, value) => {
    clearEntryFieldError(id, field);
    setEntries((prev) =>
      prev.map((e) => (e.id === id ? { ...e, [field]: value } : e)),
    );
  };

  const addEntry = () =>
    setEntries((prev) => [...prev, emptyEntry(defaultUnit)]);

  const removeEntry = (id) =>
    setEntries((prev) =>
      prev.length > 1 ? prev.filter((e) => e.id !== id) : prev,
    );

  const canSave = selectedYear && organizationId && organizationId !== "all";

  const doClose = () => {
    setOrganizationId("all");
    setSelectedYear("");
    setSelectedReportingYearId("");
    setSelectedReportingYear(null);
    setShowYearError(false);
    setEntries([emptyEntry(defaultUnit)]);
    clearErrors();
    setCreateDialogPreset(null);
    setShowCreateEntriesDialog(false);
  };

  const handleClose = (v) => {
    if (!v) {
      doClose();
    }
  };

  const handleSave = async () => {
    const isYearValid = !!selectedYear;
    setShowYearError(!isYearValid);

    const areEntriesValid = validateEntries(entries, inputType, {
      validations: activeDisclosure?.validations,
    });
    if (!isYearValid || !areEntriesValid) return;

    const apiOrgId = toApiOrgId(organizationId, user?.companyId);
    const payload = entries.map((entry) => {
      const payloadEntry = { ...entry };
      const reportingMonth = payloadEntry.reportingMonth || "";
      delete payloadEntry.id;
      delete payloadEntry.reportingMonth;

      return {
        ...payloadEntry,
        orgId: apiOrgId,
        reportingDate: reportingMonth,
        disclosureId: _id,
      };
    });

    const success = await bulkCreateDataEntries(payload);
    if (success) doClose();
  };

  return (
    <AppDialog
      open={showCreateEntriesDialog}
      onOpenChange={handleClose}
      isDirty={isDirty}
      header={
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            {code && <Badge>{code}</Badge>}
            <span className="text-lg font-semibold">{title}</span>
          </div>
          {carbonEmissionMultiplier != null && (
            <p className="text-xs text-muted-foreground">
              {t("Carbon Emission Multiplier")}: {carbonEmissionMultiplier}
            </p>
          )}
        </div>
      }
      size="xl"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => handleClose(false)}>
            {t("Cancel")}
          </Button>
          <Button disabled={!canSave || creatingEntries} onClick={handleSave}>
            {t("Save Changes")}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="flex flex-wrap gap-3 items-start">
          <SelectField
            label={t("Reporting Year")}
            options={reportingYearOptions}
            value={selectedReportingYearId}
            onValueChange={(value) => {
              const selectedReporting = reportingYears.find(
                (ry) => String(getReportingYearId(ry)) === String(value),
              );

              setSelectedReportingYearId(value);
              setSelectedYear(
                selectedReporting?.startYear
                  ? String(selectedReporting.startYear)
                  : "",
              );
              setOrganizationId("all");
              setShowYearError(false);
              setSelectedReportingYear(selectedReporting || null);
            }}
            placeholder={t("Select reporting year")}
            required
            error={yearError}
            emptyLabel={t("No reporting years available")}
            sortOptions={false}
            className="w-full sm:w-60"
          />

          <div className="space-y-2">
            <Label>
              {t("Organization")}
              <span className="ml-1 text-destructive">*</span>
            </Label>
            <OrgTreeSelect
              tree={availableOrgTree}
              value={organizationId}
              onChange={(v) => {
                setOrganizationId(v);
                setShowYearError(false);
              }}
              placeholder={
                selectedYear
                  ? t("Select organization")
                  : t("Select reporting year first")
              }
              showAllOption={false}
              className="w-full"
              disabled={!selectedYear}
            />
          </div>
        </div>

        <div className="rounded-lg border border-border overflow-hidden">
          <EntryRowTable
            entries={entries}
            inputType={inputType}
            unitOptions={unitOptions}
            disclosure={activeDisclosure}
            errors={errors}
            showInlineErrors
            onUpdate={updateEntry}
            onRemove={removeEntry}
            masterDataYear={selectedYear}
            reportingBoundary={reportingBoundary}
            selectedReportingYear={selectedReportingYear}
          />
        </div>

        <div className="flex justify-end">
          <Button variant="outline" size="sm" onClick={addEntry}>
            <Plus size={14} />
            {t("Add Entry")}
          </Button>
        </div>
      </div>
    </AppDialog>
  );
});

export default CreateEntriesDialog;
